import { OnboardingCelebrationConfig, OnboardingLayout, OnboardingTheme } from '../interfaces/onboarding.interfaces';

/**
 * Configuración de UI del onboarding que el backend AÚN NO envía (theme, layout, textos de header,
 * celebración, etc.). El `GET /api/onboarding/status` solo devuelve el contrato base
 * (progress, tasks, guided_tour). El normalizador de `OnboardingService` fusiona estos valores con la
 * respuesta cruda para que la UI dinámica (banner por fase + modal de celebración) tenga todo lo que
 * necesita para renderizar.
 *
 * Los valores provienen de los mockups de Figma (antes vivían en `onboarding.mock.ts`, ya eliminado).
 *
 * TODO(backend): cuando el endpoint devuelva estos campos, esta config y el normalizador se retiran.
 */
export interface PhaseUiConfig {
    layout: OnboardingLayout;
    theme: OnboardingTheme;
    counter_label?: string;
    subtitle: string;
    time_left_label: string;
    celebration_config?: OnboardingCelebrationConfig;
}

/** Config de UI por `phase_id`. El backend aporta title/timeframe_label/order/progress/tasks. */
export const PHASE_UI_CONFIG: Record<string, PhaseUiConfig> = {
    phase_1: {
        layout: 'vertical',
        theme: 'purple',
        subtitle: 'Empecemos.',
        time_left_label: '~ 9 min left',
        celebration_config: {
            eyebrow: 'FASE 1 COMPLETADA',
            title: 'Tu negocio ya está en Fina',
            primary_cta: { label: 'Continuar', link: '/dashboard' },
            secondary_cta: { label: 'Más tarde', action: 'dismiss' },
        },
    },
    phase_2: {
        layout: 'horizontal',
        theme: 'purple',
        subtitle: 'Conoce las herramientas de Fina.',
        time_left_label: '~ 7 min left',
        // Última fase (FINA-3377 eliminó la Fase 3): su cierre completa el onboarding. Copy del antiguo cierre
        // de la Fase 3 con el subtítulo adaptado; solo felicitación, sin CTA de crédito. Lo abre `completion$`.
        celebration_config: {
            eyebrow: '¡ONBOARDING COMPLETADO!',
            title: '¡Tu negocio está listo para el siguiente nivel!',
            subtitle: 'Completaste la configuración de tu negocio en Fina.',
            primary_cta: { label: 'Ir al Dashboard Principal', link: '/dashboard' },
        },
    },
};

/** Fallback si el backend agrega una fase sin config conocida (deriva layout/theme por `order`). */
export function defaultPhaseUiConfig(order: number): PhaseUiConfig {
    return {
        layout: order === 1 ? 'vertical' : 'horizontal',
        theme: 'purple',
        subtitle: '',
        time_left_label: '',
    };
}

/**
 * `task_id` (estable) → clave de módulo UI, para que `resolveModuleIcon` elija el ícono correcto.
 * El backend envía `module` con sus propias claves (`inventory`, `bank_accounts`, `pos`, `reports`…),
 * que no existen en `MODULE_ICON_MAP`; y en la Fase 3 repite `module: "reports"` en 3 tareas distintas,
 * así que mapear por `module` perdería el ícono. Mapeamos por `task_id` (único) para reproducir Figma.
 */
export const TASK_MODULE_MAP: Record<string, string> = {
    // Fase 1
    add_first_product: 'producto',
    create_and_fund_account: 'cuenta_bancaria',
    make_first_sale: 'venta',
    // Fase 2
    create_price_list: 'canales',
    create_combo: 'combos',
    invite_associated_user: 'usuarios',
    add_first_expense: 'gastos',
    open_and_close_cashbox: 'caja',
    process_first_return: 'devoluciones',
};

/**
 * `task_id` (blueprint) → `instructionsSetName` del video tutorial que su módulo auto-muestra al entrar.
 *
 * Uso: cuando la tarea de onboarding asociada YA está completada, el orquestador suprime ese auto-show y
 * marca "no volver a mostrar" para esa key (ver `tour-orchestrator._isCompletedTaskVideo` + el hook
 * `registerCompletedVideoCheck` de `InstructionsModalService`). Centraliza la relación tarea↔video en un solo
 * lugar (Cero Cables): los componentes de negocio siguen llamando `showModal(key)` sin saber nada de onboarding.
 *
 * Solo se listan tareas cuyo módulo dispara un auto-video de bienvenida. Las keys `*-es-inventory` /
 * `*-es-sales-channels` (empty-state, en `instructionsRequired`) NO se mapean: son guías obligatorias, no el
 * video tutorial. Una `key` sin tarea (o cuya tarea no está en el estado) → no-op (no se suprime nada).
 */
export const TASK_VIDEO_KEY_MAP: Record<string, string> = {
    // Fase 1
    add_first_product: 'inventory',
    create_and_fund_account: 'bank-accounts',
    make_first_sale: 'direct-sales',
    // Fase 2
    add_first_expense: 'financial-summary',
    create_extra_charge: 'staff',
    create_combo: 'product-template',
    create_price_list: 'sales-channels',
    invite_associated_user: 'associated-users',
    process_first_return: 'devolutions',
};
